import { Col, FormGroup, Input, Label, Row, Button } from "reactstrap";
import Select from "react-select";
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import "./Search.css";
import { useTranslation } from "react-i18next";
import { tField } from "../../../utils/helpers";

function HomeSearchForm({ categories, states }) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [formValues, setFormValues] = useState({
    property_for: 'rent',
    category_id: '',
    city_ids: [],
    min_price: '',
    max_price: ''
  });
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const loadSavedData = () => {
      try {
        const result = localStorage.getItem('search_form_data');
        if (result) {
          const savedData = JSON.parse(result);
          setFormValues(savedData);
        }
      } catch (error) {
        console.log('No saved data found');
      } finally {
        setIsLoading(false);
      }
    };

    loadSavedData();
  }, []);

  // Save form values whenever they change
  useEffect(() => {
    if (!isLoading) {
      try {
        localStorage.setItem('search_form_data', JSON.stringify(formValues));
      } catch (error) {
        console.error('Error saving data:', error);
      }
    }
  }, [formValues, isLoading]);

  const customSelectStyles = {
    control: (provided, state) => ({
      ...provided,
      border: state.isFocused ? '1px solid #077b7a' : '1px solid #e5e7eb',
      borderRadius: '12px',
      boxShadow: state.isFocused ? '0 0 0 3px rgba(7, 123, 122, 0.1)' : 'none',
      '&:hover': {
        borderColor: '#077b7a'
      },
      minHeight: '48px',
      background: '#ffffff',
      transition: 'all 0.2s ease'
    }),
    singleValue: (provided) => ({
      ...provided,
      color: '#1f2937',
      fontWeight: '500'
    }),
    placeholder: (provided) => ({
      ...provided,
      color: '#9ca3af',
      fontWeight: '400'
    }),
    multiValue: (provided) => ({
      ...provided,
      backgroundColor: '#e0f2f1',
      borderRadius: '6px'
    }),
    multiValueLabel: (provided) => ({
      ...provided,
      color: '#077b7a',
      fontWeight: '500'
    }),
    multiValueRemove: (provided) => ({
      ...provided,
      color: '#077b7a',
      ':hover': {
        backgroundColor: '#077b7a',
        color: 'white'
      }
    }),
    option: (provided, state) => ({
      ...provided,
      backgroundColor: state.isSelected 
        ? '#077b7a' 
        : state.isFocused 
        ? '#f0fdfd' 
        : '#ffffff',
      color: state.isSelected ? '#ffffff' : '#1f2937',
      padding: '12px 16px',
      fontWeight: state.isSelected ? '500' : '400',
      fontSize: 15,
      cursor: 'pointer',
      transition: 'all 0.15s ease'
    }),
    menu: (provided) => ({
      ...provided,
      borderRadius: '12px',
      border: '1px solid #e5e7eb',
      boxShadow: '0 10px 30px rgba(0, 0, 0, 0.1)',
      overflow: 'hidden'
    })
  };

  const convertToEnglishDigits = (value) => {
    return value
      .replace(/[٠-٩]/g, d => d.charCodeAt(0) - 0x0660)
      .replace(/[۰-۹]/g, d => d.charCodeAt(0) - 0x06F0)
      .replace(/[^0-9]/g, '');
  };

  const handleSearch = () => {
    const searchParams = new URLSearchParams();
    searchParams.append('property_for', formValues.property_for);
    if (formValues.category_id) {
      searchParams.append('category_id', formValues.category_id);
    }
    if (formValues.city_ids.length > 0) {
      formValues.city_ids.forEach(cityId => {
        searchParams.append('city_ids[]', cityId);
      });
    }
    if (formValues.min_price) {
      searchParams.append('min_price', formValues.min_price);
    }
    if (formValues.max_price) {
      searchParams.append('max_price', formValues.max_price);
    }
    navigate(`/search?${searchParams.toString()}`);
  };

  const getSelectedCityOptions = () => {
    const selectedOptions = [];
    states.forEach(state => {
      state.cities.forEach(city => {
        if (formValues.city_ids.includes(city.id)) {
          selectedOptions.push({
            value: city.id,
            label: tField(city, 'name')
          });
        }
      });
    });
    return selectedOptions;
  };

  // Get selected category option for the Select component
  const getSelectedCategoryOption = () => {
    const category = categories.find(cat => cat.id === formValues.category_id);
    return category ? { value: category.id, label: tField(category, 'name') } : null;
  };

  return(
    <Row className="justify-content-center">
      <Col lg="6" md="8" xs="12">
        <div className="text-center mb-4">
          <h1 className="search-title">{t('home.search.title')}</h1>
        </div>
        
        <div className="search-form-card">
          {/* Property Type Tabs */}
          <div className="property-type-tabs">
            <div className="tabs-container">
              <Input
                type="radio"
                name="property_for"
                id="sale"
                className="tab-input"
                onChange={() => setFormValues({ ...formValues, property_for: 'sale' })}
                checked={formValues.property_for === 'sale'}
              />
              <Label for="sale" className="tab-label">
                { t('home.search.purpose.sale') }
              </Label>

              <Input
                type="radio"
                name="property_for"
                id="rent"
                className="tab-input"
                onChange={() => setFormValues({ ...formValues, property_for: 'rent' })}
                checked={formValues.property_for === 'rent'}
              />
              <Label for="rent" className="tab-label">
                { t('home.search.purpose.rent') }
              </Label>

              <Input
                type="radio"
                name="property_for"
                id="exchange"
                className="tab-input"
                onChange={() => setFormValues({ ...formValues, property_for: 'exchange' })}
                checked={formValues.property_for === 'exchange'}
              />
              <Label for="exchange" className="tab-label">
                { t('home.search.purpose.exchange') }
              </Label>
            </div>
          </div>

          {/* Form Fields */}
          <div className="form-fields">
            <FormGroup>
              <Select
                placeholder={ t('home.search.fields.category.placeholder') }
                options={[
                  { value: '', label: t('home.search.fields.category.all') },
                  ...categories.map(cat => ({ value: cat.id, label: tField(cat, 'name') }))
                ]}
                value={getSelectedCategoryOption()}
                onChange={(option) => setFormValues({ ...formValues, category_id: option.value })}
                styles={customSelectStyles}
                isSearchable={false}
              />
            </FormGroup>

            <FormGroup>
              <Select
                placeholder={ t('home.search.fields.city.placeholder') }
                options={states.map(state => ({
                  label: tField(state, 'name'),
                  options: state.cities.map(city => ({
                    value: city.id,
                    label: tField(city, 'name'),
                  })),
                }))}
                value={getSelectedCityOptions()}
                onChange={(selectedOptions) => {
                  setFormValues({
                    ...formValues,
                    city_ids: selectedOptions.map(opt => opt.value),
                  });
                }}
                styles={customSelectStyles}
                isMulti
                closeMenuOnSelect={false}
              />
            </FormGroup>

            <FormGroup>
              <Row>
                <Col xs="6">
                  <Input
                    type="text"
                    inputMode="numeric"
                    placeholder={t('home.search.fields.min_price.placeholder')}
                    value={formValues.min_price}
                    onChange={(e) => setFormValues({ ...formValues, min_price: convertToEnglishDigits(e.target.value) })}
                  />
                </Col>
                <Col xs="6">
                  <Input
                    type="text"
                    inputMode="numeric"
                    placeholder={t('home.search.fields.max_price.placeholder')}
                    value={formValues.max_price}
                    onChange={(e) => setFormValues({ ...formValues, max_price: convertToEnglishDigits(e.target.value) })}
                  />
                </Col>
              </Row>
            </FormGroup>

            <Button
              className="search-btn"
              block
              onClick={handleSearch}
            >
              { t('home.search.button') }
            </Button>
          </div>
        </div>
      </Col>
    </Row>
  );
}

export default HomeSearchForm;